/**
 * ESCHER FALLS — 音の土台。AudioContext と2本の音量つまみだけを持つ。
 *
 * ## 音声ファイルを使わない理由
 * すべて Web Audio で**合成**する。
 * - アプリに同梱する容量が増えない（完全オフラインを保てる）
 * - 素材の権利処理が要らない
 * - 石が擦れる音も水音も、この作品では「紙と線」の意匠に合う程度の
 *   ごく地味な音でよく、合成で足りる
 *
 * ## iOS でいちばん大事なこと
 * **AudioContext は利用者の操作の中でしか起こせない。**
 * 起動時に作っても `suspended` のまま黙り続ける。
 * したがって `unlock()` を最初の pointerdown / keydown から呼ぶ。
 * それまでは AudioContext を**作らない**（作ると suspended のゴミが残る）。
 *
 * ## 消音スイッチについて
 * WKWebView の Web Audio はアプリの音声セッションに従う。既定では消音スイッチで
 * 完全に黙るので、**`ios/App/App/AppDelegate.swift` で `.playback` に設定してある**
 * （実機で「音が全く聞こえない」となった原因はこれだった）。
 * 消音を尊重させたいなら、あちらを `.ambient` に変える。
 *
 * ## つまみは2本
 * BGM と SE を別系統にして、それぞれの `GainNode` に通す。
 * 呼び出し側は音量を一切気にしない（`bgmBus()` / `seBus()` に繋ぐだけ）。
 */

import { loadAudioSettings, type AudioSettings } from '../game/settings';

let ctx: AudioContext | null = null;
let master: GainNode | null = null;
let bgmGain: GainNode | null = null;
let seGain: GainNode | null = null;

let settings: AudioSettings = loadAudioSettings();

/** 音量つまみの値 (0..1) を実際のゲインに写す。 */
function toGain(v: number): number {
  // 耳は対数的なので、つまみを二乗して低音量側の分解能を稼ぐ。
  // 線形だと 0.2 と 0.4 の差がほとんど聞き分けられない。
  const clamped = Math.min(1, Math.max(0, v));
  return clamped * clamped;
}

/**
 * 音を使えるようにする。**利用者の操作の中から呼ぶこと。**
 *
 * **一度で成功する前提を置いてはいけない。** 実機で「音が全く鳴らない」という
 * 報告を受けた。最初の1回に賭けて `{ once: true }` で外していたのが危うく、
 * 失敗しても二度目が無い作りだった。いまは走っていることを確かめるまで
 * 毎回の操作で呼び直す（`isRunning()` を見て呼び出し側が判断できる）。
 *
 * @returns 解錠を試みたあとに走っているか。`resume()` は非同期なので
 *          直後は false のことがある（`onceRunning` で待てる）。
 */
export function unlock(): boolean {
  if (ctx === null) {
    // Safari は webkitAudioContext のまま。型に無いので取り出して使う。
    const Ctor =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (Ctor === undefined) return false; // 音が使えない環境。遊びは続く
    ctx = new Ctor();
    master = ctx.createGain();
    master.gain.value = 1;
    master.connect(ctx.destination);
    bgmGain = ctx.createGain();
    seGain = ctx.createGain();
    bgmGain.gain.value = toGain(settings.bgm);
    seGain.gain.value = toGain(settings.se);
    bgmGain.connect(master);
    seGain.connect(master);
  }
  if (ctx.state === 'suspended') {
    void ctx.resume().then(() => {
      for (const fn of runningWaiters.splice(0)) fn();
    });
  } else if (ctx.state === 'running') {
    for (const fn of runningWaiters.splice(0)) fn();
  }
  return ctx.state === 'running';
}

const runningWaiters: Array<() => void> = [];

/**
 * 走り出したら一度だけ呼ぶ。すでに走っていれば即座に呼ぶ。
 * `resume()` が非同期なので、BGM の開始はこれで待つ必要がある。
 */
export function onceRunning(fn: () => void): void {
  if (ctx !== null && ctx.state === 'running') {
    fn();
    return;
  }
  runningWaiters.push(fn);
}

/** 音が実際に走っているか。設定画面の状態表示と、解錠の打ち切り判定に使う。 */
export function isRunning(): boolean {
  return ctx !== null && ctx.state === 'running';
}

/** 診断用の文字列。利用者に「鳴らない」と言われたときに何を見ればよいか。 */
/** 音の状態。表示する語は `ui/i18n.ts` が持つ。 */
export type AudioStateKey = 'locked' | 'running' | 'suspended';

export function audioStateLabel(): AudioStateKey {
  if (ctx === null) return 'locked';
  return ctx.state === 'running' ? 'running' : 'suspended';
}

/** 使える AudioContext。まだ解錠されていなければ null。 */
export function audioContext(): AudioContext | null {
  return ctx;
}

/** BGM を繋ぐ先。解錠前は null。 */
export function bgmBus(): GainNode | null {
  return bgmGain;
}

/** 効果音を繋ぐ先。解錠前は null。 */
export function seBus(): GainNode | null {
  return seGain;
}

/**
 * 検証から出力を測るための口（`__dbg.audio.seBus`）。
 * `seBus` と同じものを返すが、名前で用途を分けておく——
 * 呼び出し側が「これは検証用だ」と分かるようにするため。
 */
export function seBusForDebug(): GainNode | null {
  return seGain;
}

/** 同じく検証用。BGM に突発音が混じっていないかを測るのに使う。 */
export function bgmBusForDebug(): GainNode | null {
  return bgmGain;
}

/** いまの音量設定。 */
export function audioSettings(): AudioSettings {
  return settings;
}

/**
 * 音量を差し替える。**保存はしない**（呼び出し側が settings.ts 経由で保存する）。
 * つまみを動かしている最中に毎回書き込むと localStorage を叩きすぎる。
 */
export function applyVolumes(next: AudioSettings): void {
  settings = next;
  if (ctx === null || bgmGain === null || seGain === null) return;
  // 急に変えるとプツッと鳴るので、ごく短く滑らせる。
  const t = ctx.currentTime;
  bgmGain.gain.setTargetAtTime(toGain(next.bgm), t, 0.02);
  seGain.gain.setTargetAtTime(toGain(next.se), t, 0.02);
}

/** タブが隠れたら止める。復帰時は `unlock()` が resume する。 */
export function suspendAudio(): void {
  if (ctx !== null && ctx.state === 'running') void ctx.suspend();
}

// =============================================================================
// 合成の小物。各音はこれらを組み合わせて作る
// =============================================================================

/**
 * 白色雑音のバッファ。石の摩擦音も水音も、元はこれを濾したもの。
 * 一度作って使い回す（毎回生成すると GC が走ってプツつく）。
 */
let noiseBuffer: AudioBuffer | null = null;

export function noise(context: AudioContext): AudioBuffer {
  if (noiseBuffer !== null && noiseBuffer.sampleRate === context.sampleRate) return noiseBuffer;
  const seconds = 2;
  const len = Math.floor(context.sampleRate * seconds);
  const buf = context.createBuffer(1, len, context.sampleRate);
  const data = buf.getChannelData(0);
  // 決定的な擬似乱数にしておく。Math.random だと版ごとに音が変わって
  // 「前より耳障りになった」の原因追跡ができない。
  let seed = 0x2f6e2b1;
  for (let i = 0; i < len; i += 1) {
    seed = (seed * 1103515245 + 12345) & 0x7fffffff;
    data[i] = (seed / 0x3fffffff) - 1;
  }
  noiseBuffer = buf;
  return buf;
}

/** 減衰する包絡線を `gain` に書き込む。attack → decay の2段だけ。 */
export function envelope(
  gain: GainNode,
  at: number,
  peak: number,
  attack: number,
  decay: number,
): void {
  gain.gain.cancelScheduledValues(at);
  gain.gain.setValueAtTime(0.0001, at);
  gain.gain.exponentialRampToValueAtTime(Math.max(0.0001, peak), at + attack);
  gain.gain.exponentialRampToValueAtTime(0.0001, at + attack + decay);
}
